import { VariantProps } from "class-variance-authority";
import { ChevronUp, Mic, MicOff } from "lucide-react";
import { forwardRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useShallow } from "zustand/react/shallow";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import * as SelectPrimitive from "@radix-ui/react-select";

import { detectChromium, getShortcutKey } from "../../utils/hotkey-helper";
import { ActionButton, ActionButtonProps } from "../action-button";

function SelectMicrophone() {
  const vidyoSDK = useVidyoStore((state) => state.vidyoSDK);

  const microphones = useVidyoStore((state) => state.microphone.available);
  const selectedMicrophone = useVidyoStore(
    useShallow((state) => ({
      id: state.microphone.selected?.id,
      name: state.microphone.selected?.name,
    })),
  );
  const setPreferredMicrophone = useVidyoStore(
    (state) => state.microphone.setPreferred,
  );

  const handleSelectMicrophone = async (microphoneId: string) => {
    const microphone = microphones.find((m) => m.id === microphoneId);
    if (microphone) {
      await vidyoSDK?.SelectLocalMicrophone({
        localMicrophone: microphone,
      });
      setPreferredMicrophone(microphone);
    }
  };

  return (
    <Select
      value={selectedMicrophone?.id}
      onValueChange={handleSelectMicrophone}
    >
      <SelectPrimitive.Trigger disabled={microphones.length === 0} asChild>
        <Button variant="ghost" size="xs" className="p-1">
          <ChevronUp className="size-4" />
        </Button>
      </SelectPrimitive.Trigger>
      <SelectContent>
        {microphones.map((microphone) => (
          <SelectItem key={microphone.id} value={microphone.id}>
            {microphone.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type ToggleMicrophoneProps = Omit<ActionButtonProps, "Icon" | "text"> &
  VariantProps<typeof ActionButton> & {
    size?: "fixed" | "auto";
  };

export const ToggleMicrophone = forwardRef<
  HTMLButtonElement,
  ToggleMicrophoneProps
>(({ size = "fixed", ...props }, ref) => {
  const isMicrophoneMuted = useVidyoStore(
    (state) => state.microphone.shouldBeMuted,
  );
  const isVidyoConnected = useVidyoStore((state) => state.isVidyoConnected);
  const toggleMicrophone = useVidyoStore((state) => state.microphone.toggle);
  const isMicrophoneServerMuted = useVidyoStore(
    (state) => state.microphone.serverMuted,
  );
  useHotkeys("mod+d", () => toggleMicrophone(), [toggleMicrophone], {
    preventDefault: true,
  });
  return (
    <ActionButton
      {...props}
      ref={ref}
      size={size}
      onClick={toggleMicrophone}
      Icon={isMicrophoneMuted ? MicOff : Mic}
      key="microphone"
      tooltipText={
        detectChromium() ? (
          isMicrophoneMuted ? (
            <span className="text-muted">{getShortcutKey()} + D</span>
          ) : (
            <span className="text-muted">{getShortcutKey()} + D</span>
          )
        ) : (
          ""
        )
      }
      text={isMicrophoneMuted ? "Unmute" : "Mute"}
      disabled={isMicrophoneServerMuted || !isVidyoConnected}
      variant={isMicrophoneMuted ? "destructive" : "default"}
      secondaryAction={<SelectMicrophone />}
    />
  );
});

ToggleMicrophone.displayName = "ToggleMicrophone";
